import { useCallback, useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { AlertTriangle, Check, CreditCard, Loader2 } from 'lucide-react'
import { supabase } from '../lib/supabase'
import { docFormatMoney } from '../locales/documents'
import type { DocLang } from '../lib/documents'
import { Button, Card } from '../ui/kit'
import { oeffneExtern } from '../lib/extern'

/**
 * Das Ende des Wegs, aus Sicht des Gasts: die Rechnung bezahlen, dort wo er sie liest.
 *
 * Bisher endete `/i/<token>` beim Ansehen. Der Gast konnte lesen und drucken und
 * musste danach eine IBAN abtippen — mit dem Handy in der Hand, drei Tage nach der
 * Abreise. Beim Angebot ist derselbe Kreis längst geschlossen (`QuoteAcceptPanel`),
 * und diese Fläche ist bewusst dessen kleiner Bruder.
 *
 * WARUM VIEL WENIGER ALS DORT: eine Rechnung nimmt man nicht an, man bezahlt sie. Es
 * gibt nichts zu entscheiden, keinen Vertragsschluss, also auch keinen Knopf, dessen
 * Beschriftung ein Gesetz vorschreibt — § 312j BGB und § 8 FAGG gelten für die
 * Bestellung, nicht für die Tilgung einer Schuld, die längst besteht. Kein Ablehnen,
 * keine Stornobedingungen, keine Übersicht: das alles steht auf dem Blatt darüber.
 *
 * OHNE VERBUNDENES STRIPE-KONTO ERSCHEINT HIER GAR NICHTS. Der kleine Vermieter mit
 * einer IBAN und ohne Stripe ist der Normalfall, nicht der Sonderfall; für ihn bleibt
 * es bei der Bankverbindung samt Verwendungszweck, die auf der Rechnung selbst steht.
 * Ein Hinweis „online bezahlen leider nicht möglich" wäre eine Sackgasse mit
 * Schleife — der Gast kann ohnehin nichts daran ändern.
 *
 * Die Sprache folgt dem DOKUMENT, nicht dem Browser: der Gast hat eine deutsche oder
 * englische Rechnung bekommen, und was darunter steht, muss dieselbe Sprache sprechen.
 */

/** Nur das, was zum Bezahlen gebraucht wird — strukturell aus `PublicDoc`. */
export interface InvoiceForPayment {
  number: string
  currency: string
  totals: { gross: number; paid: number }
  issuer: { name: string; email: string | null; phone: string | null }
  /** Ob der Betrieb online kassieren kann. Kommt aus `public_document`, weil es an
   *  `tenants.stripe_*` hängt — Spalten, die der Gast nie zu sehen bekommt. */
  payment: { online_payment: boolean } | null
}

export default function InvoicePayPanel({
  doc, token, lang, onChanged,
}: {
  doc: InvoiceForPayment
  token: string
  lang: DocLang
  onChanged: () => void
}) {
  const de = lang === 'de'
  const [params] = useSearchParams()
  const [busy, setBusy] = useState<'pay' | 'sync' | null>(null)
  const [error, setError] = useState<string | null>(null)
  /** Der Abgleich lief und Stripe hat die Zahlung (noch) nicht bestätigt. */
  const [pending, setPending] = useState(false)
  /** In DIESEM Besuch bezahlt — nur dann ist eine Bestätigung hier eine Nachricht.
   *  Eine längst beglichene Rechnung sagt das selbst, mit der Quittungszeile. */
  const [justPaid, setJustPaid] = useState(false)

  const money = (n: number) => docFormatMoney(lang, n, doc.currency)
  const issuerName = doc.issuer.name.replace(/\.$/, '')

  // Dieselbe Rechnung wie im Beleg darüber und in `BillingView.outstandingOf`:
  // Gesamtbetrag minus allem, was auf dieser Buchung schon bezahlt wurde. Der Server
  // rechnet sie noch einmal — was hier steht, entscheidet nur, was angezeigt wird.
  const outstanding = Math.round((doc.totals.gross - doc.totals.paid) * 100) / 100
  const settled = outstanding <= 0.004
  const partly = doc.totals.paid > 0.004 && !settled

  const call = useCallback(async (action: string) => {
    if (!supabase) throw new Error('no_backend')
    const { data, error: e } = await supabase.functions.invoke('invoice-pay', {
      body: { action, token },
    })
    if (e) {
      // Der Fehlercode steckt im Rumpf der Antwort, nicht in der Message —
      // ohne dieses Auspacken läse der Gast „non-2xx status code".
      const res = (e as { context?: Response }).context
      if (res && typeof res.json === 'function') {
        const parsed = await res.json().catch(() => null) as { error?: string } | null
        if (parsed?.error) throw new Error(parsed.error)
      }
      throw new Error(e.message)
    }
    return data as Record<string, unknown>
  }, [token])

  // Rückkehr von der Stripe-Seite. Die URL beweist nichts — gefragt wird der Server,
  // der seinerseits Stripe fragt. Erst danach wird aus der Rechnung eine Quittung.
  useEffect(() => {
    if (params.get('paid') !== '1' || settled) return
    setBusy('sync')
    void (async () => {
      try {
        const out = await call('sync')
        // `onChanged` lädt das Dokument neu: erst dadurch verschwindet die
        // Bankverbindung und erscheint „Betrag dankend erhalten am …".
        if (out.paid === true) { setJustPaid(true); onChanged() }
        else setPending(true)
      } catch {
        // Der Gast hat bezahlt oder nicht; ein Fehler hier ändert nichts daran. Was
        // wirklich gilt, steht in Stripe und im nächsten Abgleich.
        setPending(true)
      }
      setBusy(null)
    })()
  }, [params, call, settled, onChanged])

  const errorText = (code: string) => {
    switch (code) {
      case 'not_payable':
        return de
          ? 'Diese Rechnung kann online nicht bezahlt werden.'
          : 'This invoice cannot be paid online.'
      case 'not_found':
        return de
          ? 'Diese Rechnung ist nicht (mehr) verfügbar.'
          : 'This invoice is no longer available.'
      case 'payment_unavailable':
        return de
          ? 'Die Bezahlseite kam gerade nicht zustande. Bitte versuchen Sie es später noch einmal — oder überweisen Sie den Betrag wie auf der Rechnung angegeben.'
          : 'The payment page could not be opened just now. Please try again later — or transfer the amount as stated on the invoice.'
      default:
        return de
          ? `Es hat nicht geklappt (${code}). Bitte versuchen Sie es noch einmal.`
          : `That did not work (${code}). Please try again.`
    }
  }

  const payNow = async () => {
    setBusy('pay'); setError(null); setPending(false)
    try {
      const out = await call('pay') as { pay_url?: string | null; payment?: string }
      if (out.pay_url) {
        // In der Hülle kehrt der Aufruf beim Schließen zurück, im Browser nie —
        // dort wird die Seite verlassen. `onChanged()` holt danach den frischen
        // Zahlungsstand; ohne das zeigt die Anwendung „nicht bezahlt", obwohl
        // gezahlt wurde.
        await oeffneExtern(String(out.pay_url))
        onChanged()
        setBusy(null)
        return
      }
      // Kein Link, aber auch kein Fehler: in der Zwischenzeit bezahlt oder das
      // Stripe-Konto ist weg. Beides klärt ein frisch geladenes Dokument.
      onChanged()
      if (out.payment !== 'paid') setError('payment_unavailable')
    } catch (e) {
      setError(String((e as Error).message))
    }
    setBusy(null)
  }

  // Eine bezahlte Rechnung braucht keine Fläche: sie IST die Quittung, und die Zeile
  // „Betrag dankend erhalten am …" steht schon auf dem Blatt darüber.
  if (settled && !justPaid) return null

  // Ohne Online-Zahlung bleibt es bei der Bankverbindung auf dem Beleg. Nur während
  // eines Abgleichs steht hier trotzdem etwas — sonst sähe der Gast nach dem Bezahlen
  // eine Sekunde lang gar nichts und wüsste nicht, ob es geklappt hat.
  if (!doc.payment?.online_payment && busy !== 'sync' && !pending) return null

  return (
    <div className="no-print px-4 pb-8 -mt-2">
      <div className="max-w-lg mx-auto">
        <Card className="p-5 space-y-3">
          {settled ? (
            <div className="flex items-center gap-2">
              <Check className="text-ok shrink-0" size={22} />
              <h2 className="font-semibold">
                {de ? 'Ihre Zahlung ist eingegangen' : 'Your payment has been received'}
              </h2>
            </div>
          ) : (
            <>
              <h2 className="font-semibold">
                {de ? 'Rechnung bezahlen' : 'Pay this invoice'}
              </h2>
              <div className="border border-border rounded-xl divide-y divide-border">
                {/* Bei einer Teilzahlung reicht der offene Betrag allein nicht: der
                    Gast erinnert sich an den Gesamtbetrag und würde sonst denken, es
                    sei die falsche Rechnung. */}
                {partly && (
                  <div className="p-3 flex items-baseline justify-between gap-3 text-sm text-muted">
                    <span>{de ? 'Bereits bezahlt' : 'Already paid'}</span>
                    <span>−{money(doc.totals.paid)}</span>
                  </div>
                )}
                <div className="p-3 flex items-baseline justify-between gap-3">
                  <span className="text-sm">{de ? 'Offener Betrag' : 'Amount due'}</span>
                  <strong className="text-lg">{money(outstanding)}</strong>
                </div>
              </div>
              <p className="text-sm text-muted">
                {de
                  ? `Sie bezahlen direkt an ${issuerName}. Verwendungszweck und Bankverbindung brauchen Sie dafür nicht.`
                  : `You pay ${issuerName} directly. No reference or bank details needed.`}
              </p>
              <Button variant="primary" className="w-full" disabled={busy !== null}
                onClick={payNow}>
                {busy !== null
                  ? <Loader2 size={16} className="animate-spin" />
                  : <CreditCard size={16} />}
                {de ? `Jetzt bezahlen · ${money(outstanding)}`
                    : `Pay now · ${money(outstanding)}`}
              </Button>
            </>
          )}

          {/* Stripe bestätigt nicht jede Zahlung im selben Moment. „Zahlen Sie noch
              einmal" wäre hier der teuerste Satz von allen — also warten lassen. */}
          {pending && !settled && (
            <p className="text-sm bg-info-soft text-info rounded-xl px-3 py-2">
              {de
                ? 'Ihre Zahlung wird noch geprüft. Bitte laden Sie diese Seite in ein paar Minuten neu — zahlen Sie nicht noch einmal.'
                : 'Your payment is still being confirmed. Please reload this page in a few minutes — do not pay again.'}
            </p>
          )}

          {error && (
            <p className="text-sm bg-danger-soft text-danger rounded-xl px-3 py-2 flex gap-2">
              <AlertTriangle size={16} className="shrink-0 mt-0.5" />
              <span>{errorText(error)}</span>
            </p>
          )}
        </Card>
      </div>
    </div>
  )
}
